// src/aiq/services/api.ts

import { SimulationConfig } from '../types';

// Configuration for the API Layer
const config: SimulationConfig = {
    useMockData: false, // Set to false to connect to your local Python backend
    serverUrl: '' // Empty means use relative path (proxied to localhost:5000 by package.json or handled by same domain)
};

// Generic Fetch Helper with Auth
const fetchWithAuth = async (endpoint: string, method: string, data?: any, token?: string) => {
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
        const response = await fetch(endpoint, {
            method,
            headers,
            body: data ? JSON.stringify(data) : undefined,
        });
        
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || `API Error: ${response.statusText}`);
        }
        return await response.json();
    } catch (error) {
        console.warn(`API Call Failed to ${endpoint}`, error);
        throw error;
    }
};

// Generic JSON Fetcher for Public/Mock Endpoints
const fetchJson = async (endpoint: string, data: any) => {
    try {
        // All AiQ-specific routes are prefixed with /api/aiq in the mock server setup
        // In production, these might map to specific backend routes
        const response = await fetch(`/api/aiq${endpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        
        if (!response.ok) {
            throw new Error(`Backend Error: ${response.statusText}`);
        }
        return await response.json();
    } catch (error) {
        console.warn('AiQ API Call Failed (Using Fallback):', error);
        throw error;
    }
};

export const api = {
    // --- REAL BACKEND CONNECTIONS (Retail Bridge) ---
    portfolio: {
        // Matches your Flask route: /api/portfolios/<portfolio_id>/manual_trade
        addTrade: async (portfolioId: string, trade: any, token: string) => {
            return await fetchWithAuth(`/api/portfolios/${portfolioId}/manual_trade`, 'POST', { trade }, token);
        },
        
        // Matches: /api/portfolios/<portfolio_id>/transactions/<transaction_id>
        deleteTrade: async (portfolioId: string, transactionId: string, token: string) => {
            return await fetchWithAuth(`/api/portfolios/${portfolioId}/transactions/${transactionId}`, 'DELETE', null, token);
        },

        // Matches: /api/watchlist/item_details/<ticker>
        getWatchlistItem: async (ticker: string, token: string) => {
            return await fetchWithAuth(`/api/watchlist/item_details/${ticker}`, 'GET', null, token);
        }
    },

    // --- AI & QUANT SERVICES (Hybrid: Real if endpoints exist, else Mock) ---
    pricing: {
        priceOption: async (params: { type: string, underlying: string, strike: number, maturity: string, spot?: number, vol?: number, rate?: number }) => {
            if (config.useMockData) {
                // Fallback Mock Logic
                await new Promise(r => setTimeout(r, 800));
                return {
                    price: (Math.random() * 10 + 5).toFixed(2),
                    delta: Math.random().toFixed(3),
                    gamma: (Math.random() * 0.1).toFixed(4),
                    vega: (Math.random() * 0.5).toFixed(3),
                    theta: (-Math.random() * 0.5).toFixed(3),
                    rho: (Math.random() * 0.1).toFixed(3)
                };
            }
            
            try {
                // Calculate Time to Maturity (T) in Years
                const diffMs = new Date(params.maturity).getTime() - Date.now();
                let years = diffMs / (1000 * 60 * 60 * 24 * 365.25);
                
                // Sanitize: Enforce a minimum positive time
                if (years <= 0) {
                    console.warn("Maturity is in the past. Clamping to minimum value.");
                    years = 0.0027; // ~1 day
                }

                // Backend expects: spot, vol, rate, time
                const payload = {
                    ...params,
                    spot: params.spot || 180.0, 
                    vol: params.vol || 0.25, 
                    rate: params.rate || 0.05,
                    time: years
                };
                
                return await fetchJson('/price-option', payload);
            } catch (e) {
                console.log("Backend unreachable, falling back to simulation.");
                // Mock Fallback
                return {
                    price: (Math.random() * 10 + 5).toFixed(2),
                    delta: (0.5 + Math.random() * 0.1).toFixed(3),
                    gamma: (0.02 + Math.random() * 0.01).toFixed(4),
                    vega: (0.15 + Math.random() * 0.05).toFixed(3),
                    theta: (-0.05 - Math.random() * 0.01).toFixed(3),
                    rho: (0.01 + Math.random() * 0.01).toFixed(3)
                };
            }
        },
        
        calculateImpliedVolatility: async (params: { type: string, underlying: string, strike: number, maturity: string, marketPrice: number, spot?: number }) => {
             if (config.useMockData) {
                await new Promise(r => setTimeout(r, 800));
                return { impliedVol: 0.225, percent: 22.5, iterations: 5 };
             }

             try {
                const diffMs = new Date(params.maturity).getTime() - Date.now();
                let years = diffMs / (1000 * 60 * 60 * 24 * 365.25);
                if (years <= 0) years = 0.0027;

                const payload = {
                    ...params,
                    spot: params.spot || 180.0,
                    rate: 0.05,
                    time: years
                };

                return await fetchJson('/calculate-iv', payload);
             } catch (e) {
                 console.error("IV Calc Error", e);
                 return { impliedVol: 0.25, percent: 25.0, iterations: 0 };
             }
        },

        getVolatilitySurface: async (params: { spot: number, atmVol: number }) => {
            // Local Simulation Logic (Frontend-only for visualization speed)
            // In production, this would fetch /api/market-data/surface
            
            const strikes = [];
            const center = Math.round(params.spot / 5) * 5;
            
            for (let i = -4; i <= 4; i++) {
                const k = center + (i * 5);
                const moneyness = Math.log(k / params.spot);
                const skew = -0.6; 
                const curvature = 2.0;
                
                let iv = params.atmVol + (skew * moneyness) + (curvature * moneyness * moneyness);
                iv = Math.max(0.01, iv);
                
                strikes.push({ 
                    strike: k, 
                    iv: parseFloat((iv * 100).toFixed(2)) 
                });
            }
            return strikes;
        }
    },

    risk: {
        runStressTest: async (scenarioId: string) => {
             if (config.useMockData) {
                await new Promise(r => setTimeout(r, 1000));
                return { impact: Math.floor(Math.random() * -5000000) };
             }
             try {
                return await fetchJson('/stress-test', { scenarioId });
             } catch (e) {
                 return { impact: Math.floor(Math.random() * -5000000) };
             }
        }
    },

    ai: {
        generateText: async (context: any, prompt: string, taskType: string) => {
            if (config.useMockData) return "AI Simulated Response";
            
            try {
                const response = await fetch('/api/aiq/ai-service', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ context, prompt, taskType })
                });
                if (!response.ok) throw new Error("AI Error");
                const data = await response.json();
                return data.text || data.result;
            } catch (e) {
                console.warn("AI Service Offline");
                return "AI Service Unavailable. Please check backend connection.";
            }
        }
    }
};